import { useEffect, useState, RefObject } from 'react';

type ScrollTarget = HTMLElement | Window;

/**
 * Finds the nearest scrollable parent element starting from the given element,
 * or returns window if no scroll container is found in the parent chain.
 * Prefers the element's scroll container over window so that when content
 * scrolls inside an overflow container (e.g. callout layout), we track that.
 */
function findScrollParent(element: HTMLElement | null): ScrollTarget {
  let parent: HTMLElement | null = element?.parentElement ?? null;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const hasScroll =
      style.overflowY === 'auto' || style.overflowY === 'scroll';
    const isScrollable = parent.scrollHeight > parent.clientHeight;

    if (hasScroll && isScrollable) {
      return parent;
    }
    parent = parent.parentElement;
  }

  const windowScrollableHeight =
    document.documentElement.scrollHeight - window.innerHeight;
  if (windowScrollableHeight > 0) {
    return window;
  }

  return window;
}

/**
 * Hook that finds and tracks the scroll parent element starting from the given element ref
 * Re-evaluates when the element ref changes
 */
export function useScrollParent(elementRef: RefObject<HTMLElement>) {
  const [scrollParent, setScrollParent] = useState<ScrollTarget | null>(null);

  useEffect(() => {
    if (elementRef.current) {
      setScrollParent(findScrollParent(elementRef.current));
    }
  }, [elementRef]);

  return scrollParent;
}
